import { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { getDealForEdit, updateDeal } from '../api';
import ImageUpload from '../components/ImageUpload';
import './CreateDealPage.css';

const MEAL_TYPES = ['all', 'Dine-in', 'Takeout', 'Delivery'];
const TERMS_PRESETS = [
  'Valid for dine-in only. One per customer. Not valid with other offers.',
  'Valid for dine-in and takeout. One redemption per customer per visit.',
  'Dine-in only. Not valid on statutory holidays. One per table.',
  'Valid any day of the week. One redemption per customer.',
  'Valid Monday–Friday only. Not valid on weekends or holidays.',
  'Valid weekends only (Saturday & Sunday). One per table.',
  'Reservation recommended. One redemption per table, regardless of party size.',
  'Custom — write your own terms',
];

const toDateInput = (iso) => iso ? iso.split('T')[0] : '';
const inDays = (n) => { const d = new Date(); d.setDate(d.getDate() + n); return d.toISOString().split('T')[0]; };

export default function EditDealPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [termsPreset, setTermsPreset] = useState('Custom — write your own terms');
  const [form, setForm] = useState(null);

  useEffect(() => {
    getDealForEdit(id)
      .then(deal => {
        const matchedPreset = TERMS_PRESETS.find(t => t !== 'Custom — write your own terms' && t === deal.terms);
        setTermsPreset(matchedPreset || 'Custom — write your own terms');
        setForm({
          title: deal.title || '',
          description: deal.description || '',
          what_you_get: deal.what_you_get || '',
          meal_type: deal.meal_type || 'Dine-in',
          original_price: deal.original_price || '',
          deal_price: deal.deal_price || '',
          max_redemptions: deal.max_redemptions || '',
          valid_until: toDateInput(deal.valid_until),
          terms: deal.terms || '',
          image_url: deal.image_url || '',
          is_active: deal.is_active,
        });
      })
      .catch(err => setError(err.message || 'Deal not found or you do not have permission to edit it.'))
      .finally(() => setLoading(false));
  }, [id]);

  const setF = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleTermsPreset = (val) => {
    setTermsPreset(val);
    if (val !== 'Custom — write your own terms') setF('terms', val);
    else setF('terms', '');
  };

  const discount = form?.original_price && form?.deal_price
    ? Math.round(((form.original_price - form.deal_price) / form.original_price) * 100)
    : 0;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (Number(form.deal_price) >= Number(form.original_price))
      return setError('Deal price must be lower than original price');
    setSaving(true); setError('');
    try {
      await updateDeal(id, {
        ...form,
        original_price: Number(form.original_price),
        deal_price: Number(form.deal_price),
        max_redemptions: form.max_redemptions ? Number(form.max_redemptions) : null,
        valid_until: new Date(form.valid_until).toISOString(),
      });
      navigate('/business/dashboard');
    } catch (err) {
      setError(err.message || 'Failed to update deal');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="container" style={{ padding: 40 }}><div className="skeleton" style={{ height: 200, borderRadius: 16 }} /></div>;
  if (!form) return <div className="container" style={{ padding: 40 }}><p style={{ color: 'var(--brand)' }}>{error}</p><Link to="/business/dashboard" className="btn btn-ghost mt-2">← Back</Link></div>;

  return (
    <div className="create-deal-page">
      <div className="container">
        <Link to="/business/dashboard" className="back-link">← Dashboard</Link>
        <div className="create-deal-grid">
          <div className="create-deal-form">
            <h1>Edit deal</h1>
            <p className="create-deal-sub">Changes will go live immediately after saving.</p>
            {error && <div className="auth-error">{error}</div>}

            <form onSubmit={handleSubmit}>
              <div className="form-section">
                <h3 className="form-section-title">Deal basics</h3>
                <div className="form-group">
                  <label>Deal title *</label>
                  <input className="form-control" value={form.title} onChange={e => setF('title', e.target.value)} required maxLength={120} />
                  <span className="form-hint">{form.title.length}/120 characters</span>
                </div>
                <div className="form-group">
                  <label>Description</label>
                  <textarea className="form-control" rows={2} value={form.description} onChange={e => setF('description', e.target.value)} />
                </div>
                <div className="form-group">
                  <label>What's included</label>
                  <textarea className="form-control" rows={2} placeholder="e.g. 2 mains · 4 garlic naan · 2 drinks" value={form.what_you_get} onChange={e => setF('what_you_get', e.target.value)} />
                </div>
                <div className="form-group">
                  <label>Meal type</label>
                  <select className="form-control" value={form.meal_type} onChange={e => setF('meal_type', e.target.value)}>
                    {MEAL_TYPES.map(m => <option key={m} value={m}>{m === 'all' ? 'All types' : m}</option>)}
                  </select>
                </div>
              </div>

              <div className="form-section">
                <h3 className="form-section-title">Pricing</h3>
                <div className="form-row-2">
                  <div className="form-group">
                    <label>Original price ($) *</label>
                    <input type="number" className="form-control" min="1" step="0.01" value={form.original_price} onChange={e => setF('original_price', e.target.value)} required />
                  </div>
                  <div className="form-group">
                    <label>Deal price ($) *</label>
                    <input type="number" className="form-control" min="0.01" step="0.01" value={form.deal_price} onChange={e => setF('deal_price', e.target.value)} required />
                  </div>
                </div>
                {discount > 0 && (
                  <div className="discount-preview">
                    <span className="dp-chip">{discount}% discount</span>
                    {discount >= 40 && <span className="dp-great">Great deal! 🔥</span>}
                  </div>
                )}
              </div>

              <div className="form-section">
                <h3 className="form-section-title">Availability</h3>
                <div className="form-row-2">
                  <div className="form-group">
                    <label>Valid until *</label>
                    <input type="date" className="form-control" value={form.valid_until} onChange={e => setF('valid_until', e.target.value)} required />
                  </div>
                  <div className="form-group">
                    <label>Max redemptions (optional)</label>
                    <input type="number" className="form-control" placeholder="Leave blank for unlimited" min="1" value={form.max_redemptions} onChange={e => setF('max_redemptions', e.target.value)} />
                  </div>
                </div>
                <div className="quick-dates">
                  <span className="quick-dates-label">Extend to:</span>
                  {[14, 30, 60, 90].map(n => (
                    <button key={n} type="button" className="quick-date-btn" onClick={() => setF('valid_until', inDays(n))}>+{n} days</button>
                  ))}
                </div>
                <div className="form-group" style={{ marginTop: 14 }}>
                  <label>Deal status</label>
                  <div style={{ display: 'flex', gap: 10 }}>
                    {[true, false].map(v => (
                      <button key={String(v)} type="button"
                        className={`meal-type-btn ${form.is_active === v ? 'active' : ''}`}
                        onClick={() => setF('is_active', v)}>
                        {v ? 'Active' : 'Paused'}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              <div className="form-section">
                <h3 className="form-section-title">Terms & conditions</h3>
                <div className="form-group">
                  <label>Choose a template</label>
                  <select className="form-control" value={termsPreset} onChange={e => handleTermsPreset(e.target.value)}>
                    {TERMS_PRESETS.map(t => <option key={t} value={t}>{t.length > 70 ? t.slice(0,70)+'…' : t}</option>)}
                  </select>
                </div>
                {termsPreset === 'Custom — write your own terms' && (
                  <div className="form-group">
                    <label>Your custom terms</label>
                    <textarea className="form-control" rows={2} value={form.terms} onChange={e => setF('terms', e.target.value)} />
                  </div>
                )}
              </div>

              <div className="form-section">
                <h3 className="form-section-title">Photo</h3>
                <ImageUpload value={form.image_url} onChange={(url) => setF('image_url', url)} />
              </div>

              <div className="create-deal-actions">
                <Link to="/business/dashboard" className="btn btn-ghost">Cancel</Link>
                <button type="submit" className="btn btn-primary btn-lg" disabled={saving}>
                  {saving ? 'Saving…' : 'Save changes →'}
                </button>
              </div>
            </form>
          </div>

          <div className="deal-preview-panel">
            <h3>Live preview</h3>
            <p className="preview-sub">How this deal appears to diners</p>
            <div className="preview-card">
              <div className="preview-img">
                {form.image_url
                  ? <img src={form.image_url} alt="" onError={e => { e.target.style.display='none'; }} />
                  : <span style={{ fontSize: 48 }}>🍽</span>
                }
                {discount > 0 && <div className="preview-badge">{discount}% off</div>}
              </div>
              <div className="preview-body">
                <div className="preview-title">{form.title || 'Deal title'}</div>
                <div className="preview-price-row">
                  {form.deal_price && <span className="preview-price">${Number(form.deal_price).toFixed(2)}</span>}
                  {form.original_price && <span className="preview-orig">${Number(form.original_price).toFixed(2)}</span>}
                </div>
                <div className="preview-footer">
                  <span className={`badge ${form.is_active ? 'badge-success' : 'badge-muted'}`}>{form.is_active ? 'Active' : 'Paused'}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
